/**
 * Icons.
 *
 * Inline SVG rather than an icon package: there are a dozen of them, they never
 * change, and shipping a whole icon library to draw twelve glyphs is a cost with
 * no benefit. All are 24×24 on a common grid, `currentColor`, and marked
 * aria-hidden — every icon in this app sits beside a text label, so announcing
 * it again would only add noise for a screen-reader user.
 */
import type { SVGProps } from 'react';

const base = (props: SVGProps<SVGSVGElement>) => ({
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  ...props,
});

export const IconDashboard = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></svg>
);
export const IconFramework = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M12 3 3 7.5l9 4.5 9-4.5L12 3Z" /><path d="M3 12.5 12 17l9-4.5" /><path d="M3 17 12 21.5 21 17" /></svg>
);
export const IconAssessment = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M9 3h6a1 1 0 0 1 1 1v1H8V4a1 1 0 0 1 1-1Z" /><path d="M8 5H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /><path d="m9 13 2 2 4-4" /></svg>
);
export const IconGap = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></svg>
);
export const IconPlan = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M9 11l2 2 4-4" /><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M8 2v4M16 2v4M3 10h18" /></svg>
);
export const IconPolicy = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7l-5-5Z" /><path d="M14 2v5h5" /><path d="M9 13h6M9 17h4" /></svg>
);
export const IconVendor = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M3 21h18" /><path d="M5 21V7l7-4 7 4v14" /><path d="M9 21v-5h6v5" /><path d="M9 10h.01M15 10h.01" /></svg>
);
export const IconReport = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7l-5-5Z" /><path d="M14 2v5h5" /><path d="M9 18v-4M12 18v-6M15 18v-3" /></svg>
);
export const IconAudit = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M12 8v4l3 2" /><circle cx="12" cy="12" r="9" /></svg>
);
export const IconVendorAssessment = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7" /><path d="M9 8h5M9 12h3" /><path d="M16 16h6M19 13l3 3-3 3" /></svg>
);
export const IconScan = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /><path d="M11 8v3l2 1.5" /></svg>
);
/** A transmitting mast — an incident is something you must REPORT, on a clock. */
export const IconIncident = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M12 13v8" /><circle cx="12" cy="10" r="2" /><path d="M8.3 6.3a5.5 5.5 0 0 0 0 7.4M15.7 6.3a5.5 5.5 0 0 1 0 7.4" /><path d="M5.5 3.5a9.5 9.5 0 0 0 0 13M18.5 3.5a9.5 9.5 0 0 1 0 13" /></svg>
);

/** A globe — the Trust Centre is the one page you show the outside world. */
export const IconGlobe = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><circle cx="12" cy="12" r="9" /><path d="M3 12h18" /><path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18Z" /></svg>
);

/** A key — personal sign-in credentials, not organisational security. */
export const IconKey = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><circle cx="8" cy="14" r="3.5" /><path d="m10.6 11.4 7.9-7.9" /><path d="m16.5 5.5 2 2M14.5 7.5l2 2" /></svg>
);

/** One control linked to many requirements — the point of the Control Library. */
export const IconCatalogue = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><rect x="3" y="10" width="5.5" height="4" rx="1" /><rect x="15.5" y="4" width="5.5" height="4" rx="1" /><rect x="15.5" y="10" width="5.5" height="4" rx="1" /><rect x="15.5" y="16" width="5.5" height="4" rx="1" /><path d="M8.5 12h3.5M12 12V6h3.5M12 12v6h3.5M12 12h3.5" /></svg>
);

/** An eye — content watch observes upstream sources and reports what moved. */
export const IconWatch = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" /><circle cx="12" cy="12" r="2.6" /></svg>
);

/** A document with ticks — the SoA is a decision recorded per control. */
export const IconChecklist = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7l-5-5Z" /><path d="M14 2v5h5" /><path d="m8.5 12.5 1.2 1.2 2.3-2.4M8.5 17l1.2 1.2 2.3-2.4" /></svg>
);

export const IconMore = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><circle cx="5" cy="12" r="1.5" fill="currentColor" /><circle cx="12" cy="12" r="1.5" fill="currentColor" /><circle cx="19" cy="12" r="1.5" fill="currentColor" /></svg>
);
export const IconSun = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>
);
export const IconMoon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" /></svg>
);
export const IconLogout = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5" /><path d="M21 12H9" /></svg>
);
export const IconChevron = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="m9 18 6-6-6-6" /></svg>
);
export const IconShield = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" /><path d="m9 12 2 2 4-4" /></svg>
);
/** Help — a question mark set in a circle. Reads as "ask for guidance". */
export const IconHelp = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><circle cx="12" cy="12" r="9" /><path d="M9.3 9.2a2.7 2.7 0 0 1 5.2 1c0 1.8-2.7 2.7-2.7 2.7" /><path d="M12 17h.01" /></svg>
);
/** Organisation hierarchy — a node with two children beneath it. */
export const IconOrg = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><rect x="9" y="2" width="6" height="5" rx="1" /><rect x="2" y="17" width="6" height="5" rx="1" /><rect x="16" y="17" width="6" height="5" rx="1" /><path d="M12 7v4M5 17v-2a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v2" /></svg>
);

/** People. */
export const IconUsers = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>
);

export const IconCredits = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><circle cx="12" cy="12" r="9" /><path d="M12 7v10M9.5 9.5h5M9.5 14.5h5" /></svg>
);

/** Translations. A glyph over its own translation, which is what the screen is. */
export const IconLanguage = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M3 5h10M8 3v2M11 5c0 4.5-2.5 8-7 9" /><path d="M6.5 10c1.2 2 3 3.5 5.5 4.5" /><path d="m13 21 4.5-11L22 21M14.8 17.5h5.4" /></svg>
);

/** Manual — an open book. Reads as "the handbook / how things work". */
export const IconBook = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M12 7v13" /><path d="M3 5a1 1 0 0 1 1-1h4a3 3 0 0 1 3 3v12a2.5 2.5 0 0 0-2.5-2H4a1 1 0 0 1-1-1Z" /><path d="M21 5a1 1 0 0 0-1-1h-4a3 3 0 0 0-3 3v12a2.5 2.5 0 0 1 2.5-2H20a1 1 0 0 0 1-1Z" /></svg>
);

/** Grid / tiles view. */
export const IconGrid = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>
);
/** List / rows view. */
export const IconList = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M8 6h13M8 12h13M8 18h13" /><path d="M3.5 6h.01M3.5 12h.01M3.5 18h.01" /></svg>
);
